import {
    DotsHorizontalIcon,
    Pencil1Icon,
    PlusCircledIcon
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import '../App.css';
import BunnyInfoItem from '../components/bunnyInfoItem';
import { LittersBunnyDatatable } from '../components/datatables/littersBunnyDatatable';
import DeleteLitterDialog from '../components/dialogs/deleteLitterDialog';
import { Button } from '../components/ui/button';
import {
    Dialog,
} from "../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Skeleton } from '../components/ui/skeleton';
import { toast } from '../components/ui/use-toast';
import { AuthContext } from '../context/AuthContext';
import api from '../lib/api';

function LitterSingle() {
    const [isLoading, setIsLoading] = useState(true);
    const [isInbreedingLoading, setIsInbreedingLoading] = useState(true);
    const { auth } = useContext(AuthContext);

    const navigate = useNavigate();
    const [litter, setLitter] = useState();
    const [inbreeding, setInbreeding] = useState();
    let { litterId } = useParams();

    const fetchLitterInformation = useCallback(() => {
        api.get(`https://skhrf-api.cmdigital.se/v1/litters?litterId=${litterId}`)
            .then(response => {

                setLitter(response?.data);
                setIsLoading(false);
            })
            .catch(error => {
                if (error.response?.status === 404) {
                    toast({
                        variant: 'destructive',
                        title: 'Något gick fel.',
                        description: 'Kullen du efterfrågade finns inte.',
                    });
                    navigate(`/farms`);
                }
                console.error('Error fetching litter:', error);
                setIsLoading(false);
            });
    }, [litterId, navigate]);

    const fetchLitterInbreeding = useCallback(() => {
        api.get(`https://skhrf-api.cmdigital.se/v1/litters/inbreeding?litterId=${litterId}`)
            .then(response => {
                setInbreeding(response?.data?.inbreeding);
                setIsInbreedingLoading(false);
            })
            .catch(error => {
                // if (error.response?.status === 404) {
                    toast({
                        variant: 'destructive',
                        title: 'Något gick fel.',
                        description: 'Det gick inte att hämta inavelsprocenten.',
                    });
                // }
                console.error('Error fetching litter:', error);
                setIsLoading(false);
            });
    }, [litterId]);

    useEffect(() => {
        fetchLitterInformation();
    }, [fetchLitterInformation]);

    useEffect(() => {
        fetchLitterInbreeding();
    }, [fetchLitterInbreeding]);

    return (
        <div className='flex w-full items-center justify-center'>
            <div className='flex w-5/6 sm:w-3/4 flex-col py-6 gap-3'>
                <div className='grid grid-cols-1 xl:grid-cols-2 items-center'>
                    <h1 className='font-medium text-2xl'>
                        {isLoading ? (<Skeleton className='h-10 w-52' />) : (
                            litter?.name
                        )}
                    </h1>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3'>
                        {(auth?.user?.superadmin || auth?.user?.bunnyadmin || (auth?.user?.id === litter?.farm.breeder)) && (
                            <>
                                <Button
                                    onClick={() => {
                                        const formattedDate = format(new Date(litter?.birth_date), 'yyyy-MM-dd');
                                        navigate(`/add-bunny?litterId=${litterId}&birthDate=${formattedDate}&fatherId=${litter?.father?.id}&motherId=${litter?.mother?.id}`);
                                    }}>
                                    <PlusCircledIcon className='mr-3' />
                                    Lägg till kaniner
                                </Button>
                                <Dialog modal={true}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant='secondary'><DotsHorizontalIcon className='mr-3' /> Verktyg</Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent className='dropdown-content-width-full'>
                                            <DropdownMenuLabel>Verktyg</DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem className='cursor-pointer' onClick={() => navigate(`/litters/${litter?.id}/edit`)}>
                                                <Pencil1Icon className='mr-3' />
                                                Ändra kullinformation
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DeleteLitterDialog litterId={litter?.id} />
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </Dialog>
                            </>
                        )}
                    </div>

                </div>
                <div className='flex gap-2'>
                    <BunnyInfoItem
                        label="Gård"
                        value={<Link className='text-blue-600 cursor-pointer hover:underline' to={`/farms/${litter?.farm?.id}`}>{litter?.farm?.name}</Link>}
                        isLoading={isLoading}
                        styling={'w-1/2	'}
                    />
                    <BunnyInfoItem
                        label="Födelsedatum"
                        value={new Date(litter?.birth_date).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        })}
                        styling={'w-1/2	'}
                        isLoading={isLoading}
                    />
                </div>
                <div className='flex gap-2'>
                    <BunnyInfoItem
                        label="Far"
                        value={<Link className='text-blue-600 cursor-pointer hover:underline' to={`/bunny/${litter?.father?.id}`}>{litter?.father?.name}</Link>}
                        styling={'w-1/2	'}
                        isLoading={isLoading}
                    />
                    <BunnyInfoItem
                        label="Mor"
                        value={<Link className='text-blue-600 cursor-pointer hover:underline' to={`/bunny/${litter?.mother?.id}`}>{litter?.mother?.name}</Link>}
                        styling={'w-1/2	'}
                        isLoading={isLoading}
                    />
                </div>
                <div className='flex gap-2'>
                    <BunnyInfoItem
                        label="Antal födda kaniner"
                        value={litter?.no_children}
                        styling={'w-1/2	'}
                        isLoading={isLoading}
                    />
                    <BunnyInfoItem
                        label="Inavelsprocent"
                        value={inbreeding !== null ? (parseFloat(inbreeding * 100)).toFixed(2) + " %" : "Inte beräknat ännu"}
                        styling={'w-1/2	'}
                        isLoading={isInbreedingLoading}
                    />
                </div>
                <div>
                    <h2 className='font-medium text-md'>Kaniner i kullen</h2>
                    <LittersBunnyDatatable bunnies={litter?.children} isLoading={isLoading} />
                </div>
            </div>
        </div>
    );
}

export default LitterSingle;
